import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Flag, CheckCircle2, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const REASON_LABELS = {
  wrong_answer: '❌ Wrong answer',
  typo: '✏️ Typo / wording',
  unclear: '❓ Unclear',
  other: '💬 Other',
};

export function AdminReportsPage() {
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('open');
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getQuestionReports(statusFilter);
      setReports(res.data.reports || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const resolve = async (id, action) => {
    try {
      await adminAPI.resolveQuestionReport(id, action);
      toast.success(action === 'deactivate' ? 'Question deactivated' : 'Report dismissed');
      load();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-['Space_Grotesk'] flex items-center gap-3">
            <Flag className="w-7 h-7 text-rose-400" /> Question Reports
          </h1>
          <p className="text-sm text-slate-400 mt-1">Learner-flagged questions for review.</p>
        </div>
        <div className="flex gap-2">
          {['open', 'resolved', 'all'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize ${statusFilter === s ? 'bg-teal-500 text-slate-950 font-semibold' : 'bg-slate-800 text-slate-400 hover:text-white'}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner className="py-16" />
      ) : reports.length === 0 ? (
        <div className="bg-[#0d1f35] border border-slate-800 rounded-2xl p-12 text-center">
          <CheckCircle2 className="w-10 h-10 text-teal-400 mx-auto mb-3" />
          <p className="text-slate-300 font-medium">All clear — no {statusFilter === 'all' ? '' : statusFilter} reports.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <div key={r._id} className="bg-[#0d1f35] border border-slate-800 rounded-2xl p-5">
              <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                <div className="flex-1">
                  <p className="text-white font-medium">{r.question_text}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {REASON_LABELS[r.reason] || r.reason} • by {r.reporter} • {new Date(r.created_at).toLocaleString()}
                    {!r.question_active && <span className="ml-2 text-rose-400">(question inactive)</span>}
                  </p>
                  {r.details && <p className="text-sm text-slate-400 mt-2 border-l-2 border-slate-700 pl-3">{r.details}</p>}
                </div>
                {r.status === 'open' && (
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => resolve(r._id, 'dismiss')}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800">
                      Dismiss
                    </Button>
                    <Button size="sm" onClick={() => resolve(r._id, 'deactivate')}
                      className="bg-rose-500/90 hover:bg-rose-500 text-white">
                      <EyeOff className="w-4 h-4 mr-1" /> Deactivate Q
                    </Button>
                  </div>
                )}
                {r.status !== 'open' && (
                  <span className="text-xs px-2 py-1 rounded-full bg-slate-800 text-slate-400 self-start">{r.resolution || r.status}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
